import React, { useRef, useCallback, useState, useEffect, useMemo } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { useTranslation } from 'react-i18next';
import { forceCollide, forceCenter } from 'd3-force-3d';
import { FACULTY_COLORS, DEFAULT_NODE_COLOR } from './graph/graphConfig';
import { useGraphInteraction } from './graph/useGraphInteraction';
import { renderUniversityNode } from './graph/nodeRenderers';
import { getCollaborationLinkColor, getCollaborationLinkWidth } from './graph/linkRenderers';
import GraphTooltip from './graph/GraphTooltip';
import GraphLegend from './graph/GraphLegend';
import GraphStatsFooter from './graph/GraphStatsFooter';
import CrossFacultyProjectsModal from './graph/CrossFacultyProjectsModal';
import FullscreenButton from './graph/FullscreenButton';
import { getCrossFacultyProjects } from '../services/apiService';
import type { UniversityGraphResponse, FacultyNode, FacultyEdge, CrossFacultyProject, CrossFacultyProjectsResponse } from '../types/api';
import type { NodeId } from '../types/graph';

interface UniversityGraphNode {
  id: string;
  label: string;
  abbreviation: string;
  peopleCount: number;
  color: string;
  x?: number;
  y?: number;
}

interface UniversityGraphLink {
  source: string;
  target: string;
  sharedProjects: number;
  crossCollaborations: number;
}

interface SelectedLink {
  sourceId: NodeId;
  targetId: NodeId;
}

interface UniversityGraphViewProps {
  data: UniversityGraphResponse;
  onFacultyClick?: (facultyId: string) => void;
}

export default function UniversityGraphView({ data, onFacultyClick }: UniversityGraphViewProps) {
  const { t } = useTranslation('network');
  const graphRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [selectedLink, setSelectedLink] = useState<SelectedLink | null>(null);
  const [crossFacultyProjects, setCrossFacultyProjects] = useState<CrossFacultyProject[]>([]);
  const [crossFacultyProjectsLoading, setCrossFacultyProjectsLoading] = useState<boolean>(false);

  const handleNodeClick = useCallback((node: any) => {
    if (node && node.id && onFacultyClick) onFacultyClick(node.id);
  }, [onFacultyClick]);

  const handleLinkClick = useCallback((link: any) => {
    const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
    const targetId = typeof link.target === 'object' ? link.target.id : link.target;
    setSelectedLink({ sourceId, targetId });
    setCrossFacultyProjects([]);
    setCrossFacultyProjectsLoading(true);
    getCrossFacultyProjects(sourceId, targetId)
      .then((res: CrossFacultyProjectsResponse) => setCrossFacultyProjects(res.projects || []))
      .catch(() => setCrossFacultyProjects([]))
      .finally(() => setCrossFacultyProjectsLoading(false));
  }, []);

  const graphData = useMemo(() => {
    const nodes: UniversityGraphNode[] = data.nodes.map((node: FacultyNode) => {
      const n: UniversityGraphNode = {
        id: node.id || node.abbreviation,
        label: node.label,
        abbreviation: node.id || node.abbreviation,
        peopleCount: node.peopleCount,
        color: FACULTY_COLORS[node.id] || FACULTY_COLORS[node.abbreviation] || DEFAULT_NODE_COLOR,
      };
      if (node.x != null && node.y != null) {
        n.x = node.x;
        n.y = node.y;
      }
      return n;
    });
    const nodeById = new Map(nodes.map(n => [n.id, n]));
    const links: UniversityGraphLink[] = data.edges
      .filter((edge: FacultyEdge) => nodeById.has(edge.source) && nodeById.has(edge.target))
      .map((edge: FacultyEdge) => ({
        source: edge.source,
        target: edge.target,
        sharedProjects: edge.sharedProjects,
        crossCollaborations: edge.crossCollaborations,
      }));
    return { nodes, links };
  }, [data]);

  const maxWeight = useMemo(() =>
    graphData.links.reduce((max, l) => Math.max(max, l.crossCollaborations || 1), 1),
  [graphData.links]);

  const [minEdgeWeight, setMinEdgeWeight] = useState<number>(1);

  const interaction = useGraphInteraction(containerRef, graphData.links);

  // Configure d3 forces
  useEffect(() => {
    const fg = graphRef.current;
    if (!fg) return;
    fg.d3Force('charge').strength(-80);
    fg.d3Force('link').distance(200).strength(0.2);
    fg.d3Force('collide', forceCollide()
      .radius((node: any) => Math.sqrt(node.peopleCount || 1) * 0.2 + 14)
      .strength(0.7));
    fg.d3Force('centerForce', forceCenter(0, 0).strength(0.02));
  }, []);

  // Zoom-to-fit after simulation settles
  const hasZoomedRef = useRef<boolean>(false);
  const handleEngineStop = useCallback(() => {
    if (hasZoomedRef.current) return;
    hasZoomedRef.current = true;
    graphRef.current?.zoomToFit(400, 60);
  }, []);

  // Reheat on slider change
  const isFirstRender = useRef<boolean>(true);
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    const fg = graphRef.current;
    if (!fg) return;
    hasZoomedRef.current = false;
    fg.d3ReheatSimulation();
  }, [minEdgeWeight]);

  return (
    <div ref={wrapperRef} className="flex-1 flex flex-col relative overflow-hidden bg-muted/30">
      <GraphStatsFooter stats={[
        { label: t('stats.faculties'), value: data.stats.totalPeople ? data.nodes.length : 0 },
        { label: t('stats.people'), value: data.stats.totalPeople || 0 },
        { label: t('stats.connections'), value: data.stats.totalEdges || 0 },
      ]} />

      <div
        ref={containerRef}
        onMouseMove={interaction.handleMouseMove}
        className="flex-1 relative bg-muted/30"
      >
        <FullscreenButton targetRef={wrapperRef} />

        <GraphLegend
          title={t('legend.title')}
          items={data.nodes.map((node: FacultyNode) => ({
            key: node.id,
            label: `${node.id} (${node.peopleCount})`,
            color: FACULTY_COLORS[node.id] || FACULTY_COLORS[node.abbreviation] || DEFAULT_NODE_COLOR,
          }))}
          minEdgeValue={minEdgeWeight}
          maxEdgeValue={maxWeight}
          onMinEdgeValueChange={setMinEdgeWeight}
          filterLabel={t('filter.minSharedOutcomes')}
          t={t}
        />

        <ForceGraph2D
          ref={graphRef}
          graphData={graphData}
          nodeId="id"
          nodeVal={(node: any) => Math.sqrt(node.peopleCount) * 1.2}
          nodeColor={(node: any) => node.color}
          nodeCanvasObject={(node: any, ctx: CanvasRenderingContext2D) => {
            const radius = Math.sqrt(node.peopleCount) * 0.2 + 10;
            const highlighted = interaction.isNodeHighlighted(node.id);
            renderUniversityNode(node, ctx, {
              isHighlighted: highlighted,
              hasHover: interaction.hasHover,
              isHovered: interaction.hoveredNode?.id === node.id,
              color: node.color,
              radius,
            });
          }}
          linkWidth={(link: any) => {
            if (interaction.hoveredLink === link) {
              const ceiling = Math.max(maxWeight, 15);
              const tw = Math.min((link.crossCollaborations || 1) / ceiling, 1);
              return (1.0 + tw * 8.5) * 2;
            }
            return getCollaborationLinkWidth(link, 'crossCollaborations', maxWeight, interaction.isLinkHighlighted(link), interaction.hasHover);
          }}
          linkColor={(link: any) => {
            if (interaction.hoveredLink === link) {
              const sourceColor = typeof link.source === 'object' ? link.source.color : DEFAULT_NODE_COLOR;
              return sourceColor;
            }
            const highlighted = interaction.isLinkHighlighted(link);
            const hoveredId = interaction.hoveredNode?.id;
            let edgeColor: string | undefined;
            if (hoveredId && highlighted) {
              const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
              const otherNode = sourceId === hoveredId ? link.target : link.source;
              edgeColor = typeof otherNode === 'object' ? otherNode.color : DEFAULT_NODE_COLOR;
            }
            return getCollaborationLinkColor(link, 'crossCollaborations', maxWeight, highlighted, interaction.hasHover, edgeColor);
          }}
          linkCurvature={0.2}
          linkCanvasObjectMode="after"
          linkCanvasObject={(link: any, ctx: CanvasRenderingContext2D) => {
            if (interaction.hoveredLink !== link) return;
            const src = typeof link.source === 'object' ? link.source : null;
            const tgt = typeof link.target === 'object' ? link.target : null;
            if (!src || !tgt || src.x == null || tgt.x == null) return;

            const mx = (src.x + tgt.x) / 2;
            const my = (src.y + tgt.y) / 2;
            const label = `${link.crossCollaborations}`;

            ctx.save();
            ctx.font = 'bold 6px Sans-Serif';
            const textW = ctx.measureText(label).width;
            const padX = 4;
            const padY = 3;
            const bx = mx - textW / 2 - padX;
            const by = my - 5 - padY;
            const bw = textW + padX * 2;
            const bh = 10 + padY * 2;
            const rr = 4;
            ctx.fillStyle = 'rgba(30, 41, 59, 0.85)';
            ctx.beginPath();
            ctx.moveTo(bx + rr, by);
            ctx.lineTo(bx + bw - rr, by);
            ctx.arcTo(bx + bw, by, bx + bw, by + rr, rr);
            ctx.lineTo(bx + bw, by + bh - rr);
            ctx.arcTo(bx + bw, by + bh, bx + bw - rr, by + bh, rr);
            ctx.lineTo(bx + rr, by + bh);
            ctx.arcTo(bx, by + bh, bx, by + bh - rr, rr);
            ctx.lineTo(bx, by + rr);
            ctx.arcTo(bx, by, bx + rr, by, rr);
            ctx.closePath();
            ctx.fill();

            ctx.fillStyle = '#fff';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(label, mx, my);
            ctx.restore();
          }}
          linkVisibility={(link: any) => {
            if (link.crossCollaborations < minEdgeWeight) return false;
            if (interaction.hasHover && !interaction.isLinkHighlighted(link)) return false;
            return true;
          }}
          onLinkClick={handleLinkClick}
          linkHoverPrecision={8}
          onNodeClick={handleNodeClick}
          onNodeHover={interaction.handleNodeHover}
          onLinkHover={interaction.handleLinkHover}
          nodeLabel=""
          linkLabel=""
          warmupTicks={50}
          cooldownTicks={50}
          onEngineStop={handleEngineStop}
          d3AlphaDecay={0.02}
          d3VelocityDecay={0.4}
          enableNodeDrag={true}
          enableZoomInteraction={true}
          enablePanInteraction={true}
        />
      </div>

      {!selectedLink && (
        <GraphTooltip
          visible={!!(interaction.hoveredNode || interaction.hoveredLink)}
          x={interaction.tooltipPos.x}
          y={interaction.tooltipPos.y}
        >
          {interaction.hoveredNode ? (
            <>
              <strong>{interaction.hoveredNode.abbreviation}</strong> — {interaction.hoveredNode.label}
              <br />
              <span className="text-[0.8em] opacity-90">
                {interaction.hoveredNode.peopleCount} {t('university.tooltip.people')}
              </span>
              {!onFacultyClick && (
                <>
                  <br />
                  <span className="text-[0.75em] opacity-85">
                    {t('university.tooltip.facultyDetailDisabled')}
                  </span>
                </>
              )}
            </>
          ) : interaction.hoveredLink ? (
            <>
              <strong>{typeof interaction.hoveredLink.source === 'object' ? interaction.hoveredLink.source.abbreviation : interaction.hoveredLink.source}</strong>
              {` ${t('crossFaculty.and')} `}
              <strong>{typeof interaction.hoveredLink.target === 'object' ? interaction.hoveredLink.target.abbreviation : interaction.hoveredLink.target}</strong>
              <br />
              <span className="text-[0.8em] opacity-90">
                {interaction.hoveredLink.sharedProjects} {t('university.tooltip.sharedProjects')}
                {', '}
                {interaction.hoveredLink.crossCollaborations} {t('university.tooltip.collaborations')}
              </span>
            </>
          ) : null}
        </GraphTooltip>
      )}

      {selectedLink && (
        <CrossFacultyProjectsModal
          facultyA={selectedLink.sourceId}
          facultyB={selectedLink.targetId}
          projects={crossFacultyProjects}
          loading={crossFacultyProjectsLoading}
          onClose={() => setSelectedLink(null)}
          t={t}
        />
      )}
    </div>
  );
}
